import React, { useState, useEffect } from 'react';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import statisticsService, { StatisticsData as BackendStatisticsData } from '@/services/statisticsService';

interface ChartData {
  date: string;
  total: number;
  completed: number;
  rejected: number;
  inProgress: number;
}

interface PeriodOption {
  value: 'day' | 'week' | 'month' | 'year';
  label: string;
  getDateRange: () => { start: Date; end: Date };
}

// Fonction pour générer des données de démonstration
const generateFallbackData = (): ChartData[] => {
  const data: ChartData[] = [];
  const today = new Date();
  
  // Générer 30 jours de données de démonstration
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    
    const baseTotal = Math.floor(Math.random() * 20) + 5;
    const completed = Math.floor(baseTotal * (0.7 + Math.random() * 0.2));
    const rejected = Math.floor(baseTotal * (0.05 + Math.random() * 0.1));
    const inProgress = Math.max(0, baseTotal - completed - rejected);
    
    data.push({
      date: format(date, 'dd/MM'),
      total: baseTotal,
      completed: completed,
      rejected: rejected,
      inProgress: inProgress
    });
  }
  
  return data;
};

const StatisticsTab: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date())
  });
  const [statisticsData, setStatisticsData] = useState<ChartData[]>([]);
  const [generalStats, setGeneralStats] = useState<BackendStatisticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [usingMockData, setUsingMockData] = useState(false);

  const periodOptions: PeriodOption[] = [
    {
      value: 'day',
      label: 'Par Jour',
      getDateRange: () => ({
        start: startOfDay(new Date()),
        end: endOfDay(new Date())
      })
    },
    {
      value: 'week',
      label: 'Par Semaine',
      getDateRange: () => ({
        start: startOfWeek(new Date(), { locale: fr }),
        end: endOfWeek(new Date(), { locale: fr })
      })
    },
    {
      value: 'month',
      label: 'Par Mois',
      getDateRange: () => ({
        start: startOfMonth(new Date()),
        end: endOfMonth(new Date())
      })
    },
    {
      value: 'year',
      label: 'Par Année',
      getDateRange: () => ({
        start: startOfYear(new Date()),
        end: endOfYear(new Date())
      })
    }
  ];

  useEffect(() => {
    // Charger directement les vraies données
   
    loadStatisticsData();
  }, [selectedPeriod, dateRange]);

  const loadStatisticsData = async () => {
    setLoading(true);
    setError('');

    try {
      
      // Charger les statistiques générales
      const generalStatsData = await statisticsService.getGeneralStats({
        start_date: format(dateRange.start, 'yyyy-MM-dd'),
        end_date: format(dateRange.end, 'yyyy-MM-dd')
      });
      
      setGeneralStats(generalStatsData);

      // Charger les données de série temporelle
      const timeSeriesData = await statisticsService.getTimeSeriesData({
        period: selectedPeriod,
        start_date: format(dateRange.start, 'yyyy-MM-dd'),
        end_date: format(dateRange.end, 'yyyy-MM-dd')
      });
      
      // Convertir les données pour les graphiques
      const chartData: ChartData[] = timeSeriesData.map(item => {
       
        return {
          date: format(new Date(item.date), 'dd/MM'),
          total: item.total || 0,
          completed: item.completed || 0,
          rejected: item.rejected || 0,
          inProgress: item.in_progress || 0
        };
      });

      
      // Vérifier si les données sont valides
      if (chartData.length === 0) {
        console.warn('⚠️ Aucune donnée de graphique générée, utilisation des données de démonstration');
        setStatisticsData(generateFallbackData());
        setUsingMockData(true);
      } else {
        setStatisticsData(chartData);
        setUsingMockData(false);
      }
    } catch (err: any) {
      console.error('❌ Erreur lors du chargement des statistiques:', err);
      setError('Erreur lors du chargement des statistiques: ' + err.message);
      
      // Utiliser les données de démonstration en cas d'erreur
      setStatisticsData(generateFallbackData());
      setUsingMockData(true);
    } finally {
      setLoading(false);
    }
  };

  // Supprimer la fonction generateMockData car nous utilisons maintenant l'API

  const handlePeriodChange = (period: 'day' | 'week' | 'month' | 'year') => {
    setSelectedPeriod(period);
    const option = periodOptions.find(opt => opt.value === period);
    if (option) {
      setDateRange(option.getDateRange());
    }
  };


  const COLORS = ['#3B82F6', '#10B981', '#EF4444', '#F59E0B'];

  return (
    <div className="p-6">
      {/* En-tête avec contrôles */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Statistiques des Pesées</h2>
          <p className="text-sm text-gray-600 mt-1">
            Période: {format(dateRange.start, 'dd/MM/yyyy', { locale: fr })} - {format(dateRange.end, 'dd/MM/yyyy', { locale: fr })}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handlePeriodChange(option.value)}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                selectedPeriod === option.value
                  ? 'bg-blue-100 text-blue-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Chargement des statistiques...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <span>{error}</span>
          </div>
        </div>
      )}

      {usingMockData && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-6">
          <div className="flex items-center">
            <i className="fas fa-info-circle mr-2"></i>
            <span>Données de démonstration utilisées - Les graphiques affichent des données simulées</span>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-8">
          {/* Indicateurs clés */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-chart-line text-blue-600 text-2xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-blue-800">Total Pesées</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {(generalStats?.total_pesées || 0).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-check-circle text-green-600 text-2xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-green-800">Complétées</p>
                  <p className="text-2xl font-bold text-green-900">
                    {(generalStats?.pesées_complétées || 0).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-times-circle text-red-600 text-2xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-red-800">Refoulées</p>
                  <p className="text-2xl font-bold text-red-900">
                    {(generalStats?.pesées_refoulées || 0).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <i className="fas fa-clock text-yellow-600 text-2xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-yellow-800">En Cours</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {(generalStats?.pesées_en_cours || 0).toLocaleString('fr-FR')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Graphique linéaire - Évolution temporelle */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Évolution Temporelle</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={statisticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2} name="Total" />
                  <Line type="monotone" dataKey="completed" stroke="#10B981" strokeWidth={2} name="Complétées" />
                  <Line type="monotone" dataKey="rejected" stroke="#EF4444" strokeWidth={2} name="Refoulées" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique en barres - Comparaison */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition par Statut</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statisticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completed" fill="#10B981" name="Complétées" />
                  <Bar dataKey="rejected" fill="#EF4444" name="Refoulées" />
                  <Bar dataKey="inProgress" fill="#F59E0B" name="En Cours" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique en aires empilées */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Volume Total</h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={statisticsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="completed" stackId="1" stroke="#10B981" fill="#10B981" name="Complétées" />
                  <Area type="monotone" dataKey="rejected" stackId="1" stroke="#EF4444" fill="#EF4444" name="Refoulées" />
                  <Area type="monotone" dataKey="inProgress" stackId="1" stroke="#F59E0B" fill="#F59E0B" name="En Cours" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique en secteurs */}
            <div className="bg-white border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Répartition Globale</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Complétées', value: generalStats?.pesées_complétées || 0 },
                      { name: 'Refoulées', value: generalStats?.pesées_refoulées || 0 },
                      { name: 'En Cours', value: generalStats?.pesées_en_cours || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {[0, 1, 2].map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatisticsTab;
